import { useState, useEffect } from "react"
import { Box, CssBaseline, Typography, IconButton } from "@mui/material"
import { Close as CloseIcon } from "@mui/icons-material"
import { keyframes } from "@mui/system"
import TopBar from "./TopBar"
import Sidebar from "./Sidebar"
import { useAuth } from "../../contexts/AuthContext"
import { FileStack } from "lucide-react"

const welcomeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`

const sparkle = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(0deg);
    opacity: 0.7;
  }

  50% {
    transform: translateY(-4px) rotate(8deg);
    opacity: 1;
  }
`

function DashboardLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const { user } = useAuth()

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem("fiscale_sidebar_width")
    return saved ? parseInt(saved, 10) : 260
  })

  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem("fiscale_sidebar_collapsed") === "true"
  })

  const [showWelcome, setShowWelcome] = useState(false)

  useEffect(() => {
    if (!user?.name) return

    const alreadyShown = sessionStorage.getItem("welcomeShown") === "true"

    if (!alreadyShown) {
      setShowWelcome(true)

      const timer = setTimeout(() => {
        setShowWelcome(false)
        sessionStorage.setItem("welcomeShown", "true")
      }, 10000)

      return () => clearTimeout(timer)
    }
  }, [user?.name])

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev)
  }

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev
      localStorage.setItem("fiscale_sidebar_collapsed", String(next))
      return next
    })
  }

  const handleWidthChange = (newWidth) => {
    setSidebarWidth(newWidth)
    localStorage.setItem("fiscale_sidebar_width", String(newWidth))
  }

  const handleCloseWelcome = () => {
    setShowWelcome(false)
    sessionStorage.setItem("welcomeShown", "true")
  }

  const activeDrawerWidth = isCollapsed ? 68 : sidebarWidth

  const firstName = user?.name?.split(" ")[0] || "";
  const capitalizeFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1);
  const hour = new Date().getHours()

  let greeting = `Good morning ${capitalizeFirstName}! `
  let message = "Hope you're having a great start to the day."

  if (hour >= 12 && hour < 17) {
    greeting = `Good afternoon ${capitalizeFirstName}! `
    message = "Hope your day is going smoothly."
  } else if (hour >= 17) {
    greeting = `Good evening ${capitalizeFirstName}! `
    message = "Nice work today. Take a moment to unwind when you can."
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "#",
      }}
    >
      <CssBaseline />

      <TopBar
        onMenuClick={handleDrawerToggle}
        drawerWidth={activeDrawerWidth}
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
      />

      <Sidebar
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerToggle}
        drawerWidth={activeDrawerWidth}
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
        onWidthChange={handleWidthChange}
        userRole={user?.role}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: {
            sm: `calc(100% - ${activeDrawerWidth}px)`,
          },
          transition: "width 0.2s ease-in-out, margin-left 0.2s ease-in-out",
          mt: 8,
          px: {
            xs: 2,
            sm: 3,
            md: 4,
          },
          py: {
            xs: 2,
            sm: 3,
          },
        }}
      >
        {showWelcome && user?.name && (
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
              px: {
                xs: 2,
                sm: 2.5,
              },
              py: 2,
              borderRadius: "10px",
              border: "1px solid #e8e2d8",
              background: "linear-gradient(105deg, #fffaf2 0%, #fffdf8 55%, #f8fbff 100%)",
              // boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.03)",
              animation: `${welcomeIn} 0.45s ease-out`,
            }}
          >
            {/* Decorative glow */}
            <Box
              sx={{
                position: "absolute",
                width: 110,
                height: 110,
                borderRadius: "50%",
                background: "rgba(251, 191, 36, 0.10)",
                top: -65,
                left: 25,
                pointerEvents: "none",
              }}
            />

            {/* Small decorative sparkle */}
            <Box
              sx={{
                position: "absolute",
                top: 9,
                left: 92,
                fontSize: "0.7rem",
                animation: `${sparkle} 2.5s ease-in-out infinite`,
              }}
            >
              ✦
            </Box>

            {/* Greeting icon */}
            {/* <Box
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 42,
                height: 42,
                flexShrink: 0,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                border: "1px solid #f5d98a",
                fontSize: "1.25rem",
                boxShadow: "0 2px 5px rgba(180, 130, 30, 0.10)",
              }}
            >
              👋
            </Box> */}

            {/* Message */}
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: {
                    xs: "1rem",
                    sm: "1rem",
                  },
                  fontWeight: 400,
                  color: "#292524",
                  lineHeight: 1.4,
                }}
              >
                {greeting}
                {/* <Box
                  component="span"
                  sx={{
                    ml: 0.6,
                    color: "#d97706",
                  }}
                >
                  ✨
                </Box> */}
              </Typography>

              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: "1rem",
                  color: "#78716c",
                  lineHeight: 1.45,
                }}
              >
                {message}
              </Typography>
            </Box>

            {/* Close */}
            <IconButton
              size="small"
              onClick={handleCloseWelcome}
              aria-label="Dismiss welcome message"
              sx={{
                position: "relative",
                zIndex: 1,
                ml: "auto",
                flexShrink: 0,
                color: "#a8a29e",
                "&:hover": {
                  color: "#57534e",
                  backgroundColor: "rgba(120, 113, 108, 0.08)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )}

        {children}
      </Box>
    </Box>
  )
}

export default DashboardLayout